"use client"

import React, { useState, useEffect } from "react"
import { Bell, AlertTriangle, XCircle, Info, Clock, MapPin, User } from "lucide-react"
import { Button } from "./button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu"
import { Badge } from "./badge"
import apiService from '../../services/apiService'
import type { Notification } from '../../types';

interface NotificationDropdownProps {
  className?: string;
  style?: React.CSSProperties;
}

export function NotificationDropdown({ className, style }: NotificationDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [, setIsOpen] = useState(false)

  // Fetch notifications when component mounts and when dropdown opens
  useEffect(() => {
    fetchNotifications()
    // Set up polling for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const response = await apiService.getNotifications({ limit: 20, hours: 24 })
      if (response.success) {
        setNotifications(response.data.notifications)
        setUnreadCount(response.data.unread_count)
      } else {
        console.error('Failed to fetch notifications:', response.error)
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationIds: string[]) => {
    try {
      const response = await apiService.markNotificationsAsRead(notificationIds)
      if (response.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(notif => 
            notificationIds.includes(notif.id) 
              ? { ...notif, is_read: true }
              : notif
          )
        )
        setUnreadCount(prev => Math.max(0, prev - notificationIds.length))
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const response = await apiService.markNotificationsAsRead([], true)
      if (response.success) {
        setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })))
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const getNotificationIcon = (type: string, severity: string) => {
    switch (type) {
      case "error":
        return <XCircle className="h-4 w-4" style={{ color: "#ef4444" }} />
      case "warning":
        return <AlertTriangle className="h-4 w-4" style={{ color: "#f59e0b" }} />
      case "alert":
        return <Bell className="h-4 w-4" style={{ color: "#ef4444" }} />
      default:
        return <Info className="h-4 w-4" style={{ color: "#3b82f6" }} />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "#ef4444"
      case "high":
        return "#f59e0b"
      case "medium":
        return "#eab308"
      default:
        return "#6b7280"
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const handleDropdownOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      fetchNotifications()
    }
  }

  return (
    <DropdownMenu onOpenChange={handleDropdownOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`border-0 hover:bg-[#1a1a1a] h-10 w-10 p-0 relative ${className}`}
          style={style}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
              style={{
                backgroundColor: "#ef4444",
                color: "#ffffff",
                fontSize: "10px",
                minWidth: "20px",
                borderRadius: "10px"
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-96 p-0 max-h-[500px] overflow-hidden"
        style={{ backgroundColor: "#1a1a1a", borderColor: "#2a2a2a" }}
      >
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: "#2a2a2a" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" style={{ color: "#94a3b8" }} />
              <h3 className="text-lg font-semibold" style={{ color: "#e2e8f0" }}>
                Notifications
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                style={{
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                  fontSize: "12px"
                }}
              >
                {unreadCount} unread
              </Badge>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs h-6 px-2"
                  style={{ color: "#94a3b8" }}
                >
                  Mark all read
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center" style={{ color: "#94a3b8" }}>
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center" style={{ color: "#94a3b8" }}>
              No recent notifications
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 border-b hover:bg-[#2a2a2a] cursor-pointer transition-colors ${
                  !notification.is_read ? "bg-[#1e1e2e]" : ""
                }`}
                style={{ borderColor: "#2a2a2a" }}
                onClick={() => {
                  if (!notification.is_read) {
                    markAsRead([notification.id])
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="mt-1 flex-shrink-0">
                    {getNotificationIcon(notification.type, notification.severity)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium truncate" style={{ color: "#e2e8f0" }}>
                        {notification.title}
                      </h4>
                      {!notification.is_read && (
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: "#3b82f6" }}
                        />
                      )}
                    </div>
                    
                    <p className="text-sm mb-2 line-clamp-2" style={{ color: "#94a3b8" }}>
                      {notification.message}
                    </p>
                    
                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-xs" style={{ color: "#6b7280" }}>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{notification.site_id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{notification.client_id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatTimestamp(notification.timestamp)}</span>
                      </div>
                    </div>
                    
                    {/* Severity Badge */}
                    <div className="mt-2">
                      <Badge
                        style={{
                          backgroundColor: `${getSeverityColor(notification.severity)}20`,
                          color: getSeverityColor(notification.severity),
                          fontSize: "10px",
                          border: `1px solid ${getSeverityColor(notification.severity)}40`
                        }}
                      >
                        {notification.severity.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t text-center" style={{ borderColor: "#2a2a2a" }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false)
                // Navigate to notifications page if you have one
                // window.location.href = '/notifications'
              }}
              className="text-sm"
              style={{ color: "#94a3b8" }}
            >
              View All Notifications
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 